

































import Vue from 'vue'
import EmployeeForm from '@/components/EmployeeForm.vue'
import { COMPANY_FETCH_COMPANY_STRUCTURE, FETCH_EMPLOYEE_BY_ID, FETCH_EMPLOYEE_METADATA } from '@/store/action-types'
import { CompanyEmployee } from '@/model/company/CompanyEmployee'
import { Department } from '@/model/companyStructure/Department'
import { Employee } from '@/model/companyStructure/Employee'
import { PlanningGroup } from '@/model/companyStructure/PlanningGroup'
import { COMPANY_GET_BASIC_COMPANY_STRUCTURE, COMPANY_GET_COMPANY_STRUCTURE_SCHOOL_LIST } from '@/store/getter-types'
import { CompanyStructure } from '@/types'
import { School } from '@/model/companyStructure/School'
import ProgressCircular from '@/components/ProgressCircular.vue'
import { EmployeeMetadataResponseItem, GetEmployeeResponse } from '@/types/service-response'
import { hexToRgba } from '@/services/hex-to-rgba'
import { ProfitCenter } from '@/model/companyStructure/ProfitCenter'
import * as securityService from '@/services/security-service'
import { KeyActionEnum, SecurityArea, SecurityComponent } from '@/types/enums'
import { keyActionService } from '@/services/key-action-service'
import { KeyAction } from '@/model/KeyAction'

class Create extends KeyAction {}
class Edit extends KeyAction {}

interface ComponentData {
  loading: boolean
  currentEmployeeItem: CompanyEmployee | undefined
  profitCenterList: ProfitCenter[]
  departmentList: Array<Department>
  planningGroupList: Array<PlanningGroup>
  employeeList: Array<Employee>
  schoolList: Array<School>

  isCreate: boolean
  isEdit: boolean
  employeeMetaData: EmployeeMetadataResponseItem | null

  activeTab: number
  keyActions: KeyAction[]
}

export default Vue.extend({
  name: 'EmployeeDetailPage',

  components: {
    EmployeeForm,
    ProgressCircular
  },

  props: {
    departmentId: Number,
    employeeId: Number
  },

  data (): ComponentData {
    return {
      loading: false,
      currentEmployeeItem: undefined,
      profitCenterList: [],
      departmentList: [],
      planningGroupList: [],
      employeeList: [],
      schoolList: [],

      isCreate: false,
      isEdit: false,
      employeeMetaData: null,

      activeTab: 0,
      keyActions: []
    }
  },

  watch: {
    $route (newVal: any): void {
      if(newVal.query.create) {
        this.isCreate = true
        this.currentEmployeeItem = undefined
      }
    },

    employeeId (newVal: number): void {
      if (newVal) {
        this.isCreate = false
        this.isEdit = false
        this.fetchEmployeeInfo()
      }
    }
  },

  computed: {
    lohnIntegration (): Boolean {
      return this.$store.state.currentUser.lohnIntegration
    },

    toolbarColor (): string {
      return hexToRgba(this.$vuetify.theme.themes.light.primary)
    },

    showInsertButton (): boolean {
      return securityService.isVisible(SecurityArea.Employee, SecurityComponent.EmployeeIconInsert)
    },

    isDisabledInsertButton (): boolean {
      return securityService.isDisabled(SecurityArea.Employee, SecurityComponent.EmployeeIconInsert)
    },

    showUpdateButton (): boolean {
      return securityService.isVisible(SecurityArea.Employee, SecurityComponent.EmployeeIconUpdate)
    },

    isDisabledUpdateButton (): boolean {
      return securityService.isDisabled(SecurityArea.Employee, SecurityComponent.EmployeeIconUpdate)
    }
  },

  methods: {
    changeActiveTab (item: number): void {
      this.activeTab = item
    },

    onKeydown (event: any): void {
      const action: any = keyActionService(event, this.keyActions)
      action && action.handle()
    },

    resolver (action: Create | Edit | null): boolean {
      if(action instanceof Create) {
        return !(this.isCreate || this.isEdit || this.lohnIntegration || this.isDisabledInsertButton) && this.activeTab === 0
      } else if(action instanceof Edit) {
        return !(!this.currentEmployeeItem || this.isCreate || this.isDisabledUpdateButton) && this.activeTab === 0
      } else {
        return false
      }
    },

    onCancelClick (): void {
      this.isEdit = false
    },

    onAddEmployeeClick (): void {
      this.$router.push({ path: '/employee-detail', query: { create: 'true' }})
      this.isCreate = true
      this.currentEmployeeItem = undefined
    },

    create (action?: KeyAction | null): void {
      if(action) {
        action.event.preventDefault()
      }

      this.onAddEmployeeClick()
    },

    onEditEmployeeClick (): void {
      this.isEdit = true
    },

    update (action?: KeyAction | null): void {
      if(action) {
        action.event.preventDefault()
      }

      this.onEditEmployeeClick()
    },

    async fetchEmployeeMetadata (): Promise<void> {
      try {
        this.loading = true
        const response: any = await this.$store.dispatch(FETCH_EMPLOYEE_METADATA)
        if(!response.IsError && response.Data) {
          this.employeeMetaData = response.Data
        }
      } catch {
      } finally {
        this.loading = false
      }
    },

    async fetchEmployeeInfo (): Promise<void> {
      this.isEdit = false
      try {
        this.loading = true
        const response: GetEmployeeResponse = await this.$store.dispatch(FETCH_EMPLOYEE_BY_ID, this.employeeId)
        if(!response.IsError && response.Mitarbeiter) {
          this.currentEmployeeItem = CompanyEmployee.createFromEmplyeeFromResponse(response.Mitarbeiter)
        }
        this.loading = false
      } catch {
        this.loading = false
      }
    }
  },

  beforeMount () {
    this.keyActions = [
      new Create(KeyActionEnum.create, this.create, this.resolver,  null),
      new Edit(KeyActionEnum.edit, this.update, this.resolver, null),
    ]
  },

  async mounted () {
    window.addEventListener('keydown', this.onKeydown)
    let data: CompanyStructure = this.$store.getters[COMPANY_GET_BASIC_COMPANY_STRUCTURE]
    if(data.employeeList.length === 0) {
      await this.$store.dispatch(COMPANY_FETCH_COMPANY_STRUCTURE).then(() => {
        data = this.$store.getters[COMPANY_GET_BASIC_COMPANY_STRUCTURE]
      })
    }

    this.profitCenterList = data.profitCenterList
    this.departmentList = data.departmentList
    this.planningGroupList = data.planningGroupList
    this.employeeList = data.employeeList
    this.schoolList = this.$store.getters[COMPANY_GET_COMPANY_STRUCTURE_SCHOOL_LIST]

    await this.fetchEmployeeMetadata()

    if(this.$route.query.create) {
      this.isCreate = true
      this.currentEmployeeItem = undefined
    } else {
      await this.fetchEmployeeInfo()
    }
  },

  beforeDestroy (): void {
    window.removeEventListener('keydown', this.onKeydown)
  }
})
